import { Route, useHistory } from "react-router-dom";
import React, { useEffect } from "react";
import { DeploymentRTSPJobsUIProvider } from "./DeploymentRTSPJobsUIContext";
import { DeploymentRTSPJobsCard } from "./DeploymentRTSPJobsCard";
import { DeploymentRTSPJobNewDialog } from "./deployment-rtsp-job-new-dialog/DeploymentRTSPJobNewDialog";
import { DeploymentRTSPJobsViewDialog } from "./deployment-rtsp-job-view-dialog/DeploymentRTSPJobsViewDialog";
import { DeploymentRTSPJobStartDialog } from "./deployment-rtsp-job-start-dialog/DeploymentRTSPJobStartDialog";

/**
 * A page that displays the DeploymentRTSPJobsCard component.
 *
 * Additionally, it provides the DeploymentRTSPJobsUIProvider component
 * which wraps the DeploymentRTSPJobsCard and provides the
 * deploymentRTSPJobsUIEvents object to the DeploymentRTSPJobsCard.
 *
 * The deploymentRTSPJobsUIEvents object contains the following functions:
 * - newDeploymentRTSPJobBtnClick: navigates to the new deployment job page
 * - openViewDeploymentRTSPJobBtnClick: navigates to the view deployment job page with the given id
 * - openStartDeploymentRTSPJobBtnClick: navigates to the start deployment job page with the given id
 *
 * The DeploymentRTSPJobsCard component will be rendered with the
 * deploymentRTSPJobsUIEvents object as a prop.
 *
 * @param {function} setKey - a function that sets the key of the current page
 * @returns {ReactElement} - the DeploymentRTSPJobsPage component
 */
export function DeploymentRTSPJobsPage({ setKey }) {
  const history = useHistory();

  // Base URL for the deployment RTSP jobs page
  const deploymentRTSPJobsPageBaseUrl =
    "/admin/deploymentDetails/deploymentRTSPJobsPage";

  // Set the current page key on component mount
  useEffect(
    () => setKey("deploymentRTSPJobs"),
    [] // Run only once on component mount
  );

  // Define UI events for the deployment RTSP jobs
  const deploymentRTSPJobsUIEvents = {
    newDeploymentRTSPJobBtnClick: () => {
      history.push(`${deploymentRTSPJobsPageBaseUrl}/new`);
    },
    openViewDeploymentRTSPJobBtnClick: id => {
      history.push(`${deploymentRTSPJobsPageBaseUrl}/${id}/view`);
    },
    openStartDeploymentRTSPJobBtnClick: id => {
      history.push(`${deploymentRTSPJobsPageBaseUrl}/${id}/deploy`);
    }
  };

  return (
    <DeploymentRTSPJobsUIProvider
      deploymentRTSPJobsUIEvents={deploymentRTSPJobsUIEvents}
    >
      {/* Route for creating a new deployment RTSP job */}
      <Route path={`${deploymentRTSPJobsPageBaseUrl}/new`}>
        {({ history, match }) => (
          <DeploymentRTSPJobNewDialog
            show={match != null}
            onHide={() => {
              history.push(deploymentRTSPJobsPageBaseUrl);
            }}
          />
        )}
      </Route>
      {/* Route for viewing a specific deployment RTSP job */}
      <Route path={`${deploymentRTSPJobsPageBaseUrl}/:id/view`}>
        {({ history, match }) => (
          <DeploymentRTSPJobsViewDialog
            show={match != null}
            id={match?.params?.id}
            onHide={() => {
              history.push(deploymentRTSPJobsPageBaseUrl);
            }}
          />
        )}
      </Route>
      {/* Route for starting a specific deployment RTSP job */}
      <Route path={`${deploymentRTSPJobsPageBaseUrl}/:id/deploy`}>
        {({ history, match }) => (
          <DeploymentRTSPJobStartDialog
            show={match != null}
            id={match?.params?.id}
            onHide={() => {
              history.push(deploymentRTSPJobsPageBaseUrl);
            }}
          />
        )}
      </Route>
      {/* Render the DeploymentRTSPJobsCard component */}
      <DeploymentRTSPJobsCard />
    </DeploymentRTSPJobsUIProvider>
  );
}
